import { createStructuredSelector, createSelector } from "reselect"
import Immutable from "immutable"
import * as Sentry from "@sentry/browser"
import sortTypes, { reverseSortDirection } from "sheets/sort-types"
import * as switches from "sheets/switches"
import sheetConstants from "sheets/constants"
import { selectIsDevTeamMode, selectUserId } from "shared/userdata/userdata-selectors"
import { decodeStyles } from "imports/styleHelperFunctions"
import { getFieldsFromColumnObject } from "app/static/frontend/sheets/helper-functions"
import sheetVars from "app/static/stylus/sheets-variables.json"
import { checkPermission } from "shared/imports/permissionFunctions"
import { selectAlignmentTypes } from "shared/stakeholder/selectors"
import { selectImmutableStancesWithCustomLabels } from "shared/stance/selectors"

const sheetsVariables = decodeStyles(sheetVars)

// --------- BEGIN Helper functions -------------- //

// get the sheet key of the global store
export const sheetSelector = (state) => state.sheet

export const createSheetKey = (sheetId) => `sheet:${sheetId}`

// get the id portion of a sheetKey, of the form sheet:1235
export const extractIdFromSheetKey = (sheetKey) => Number(sheetKey.replace("sheet:", ""))

// get the sheet Id
// if the id isn't available in props, get it from params
export const selectSheetId = (state, props) => {
    if (props.id) {
        return props.id
    } else if (props.sheetId) {
        return createSheetKey(props.sheetId)
    } else if (props.params && props.params.sheetId) {
        return createSheetKey(props.params.sheetId)
    } else if (props.tableId) {
        // resource table display functions receive tableId
        return props.tableId
    } else {
        console.warn("returning undefined for some reason")
        return undefined
    }
}
/**
 * @return {String} - the sheet's numerical id (in comparison selectSheetId returns 'sheet:'+id)
 */
export const selectBasicSheetId = (state, props) =>
    props.id || props.sheetId || (props.params && props.params.sheetId) || props.tableId

// from Props selectors
export const selectDataTypeFromProps = (_, props) => props.dataType

export const selectSheetIdFromProps = (_, props) => props.sheetId

export const selectSheetIdFromParams = (_, props) => props.params.sheetId

// for ResourceTables, we need to get the columns from props because
// we want to support passing functions to different parts of Sheets from the
// ResourceTable configuration, and we can't store those in the store
export const selectColumnsFromProps = (_, { columns = Immutable.fromJS([]) }) => Immutable.fromJS(columns)

// grab the sheetKey using the url params or from the sheetId prop
export const selectSheetKey = createSelector(
    (_, props) => (props.sheetId ? props.sheetId : props.params ? props.params.sheetId : props.sheetId),
    (sheetId) => createSheetKey(sheetId),
)

// --------- End Helper functions -------------- //

// get a particular sheet from the global sheet state
// for example, when we're on sheet/4212/, then we'd want to get "sheet:4212" from the store
export const selectSheetStateWithId = createSelector(sheetSelector, selectSheetId, (state, sheetId) =>
    state.get(sheetId),
)

export const selectWindowWidth = createSelector(sheetSelector, (sheet) => sheet.get("windowWidth"))

export const selectWindowHeight = createSelector(sheetSelector, (sheet) => sheet.get("windowHeight"))

/**
 * This function returns a particular key in the current's sheet's top level state
 * selects a key in the slice of the store representing a particular state
 * For example, the key "sheet:4212" is an object with UI state, and a sheet object.
 * Get a piece of UI state, or anything that lives in the top level of "sheet:4212"
 * @name makeSheetStateSelector
 * @function
 * @param {Map} state - a Map representing the current sheet - containing both UI state and canonical data
 * @returns {Any}
 */
export const makeSheetStateSelector = (key, defaultValue = undefined) =>
    createSelector(selectSheetStateWithId, (state) => (state ? state.get(key, defaultValue) : undefined))

// selects a key in the "sheet" state of a particular sheet
// For example, the key "sheet:4212" is an object with UI state, and a sheet object. Get that sheet
// object, which represent the response from the server. This data lives one level deeper than the UI
// state for a given sheet
export const makeSheetPropSelector = (prop) =>
    createSelector(selectSheetStateWithId, (state) => (state ? state.getIn(["sheet", prop]) : undefined))

// -------------------- BEGIN UI state selectors ------------ //
// get the loading indicator for when we are fetching a sheet
// informs the spinning loading indicator at the top left hand corner of the sheet
export const selectSheetIsLoading = makeSheetStateSelector("loading")

export const selectIsLoadingBigSheetData = makeSheetStateSelector("loadingBigSheetData")

// determines whether to show the initial loading spinner. This fields gets set to false upon receiving
// responses from the server, and is never again set to true
export const selectInitialSheetRequestLoading = makeSheetStateSelector("initialSheetRequestLoading")

export const selectSuppressEmptyState = makeSheetStateSelector("suppressEmptyState")

// determine if we should override loading spinner state for Resource Tables
export const selectShouldShowLoadingSpinnerOverride = makeSheetStateSelector("shouldShowLoadingSpinnerOverride")

// what row is clicked in the sheet
export const selectClickedRow = makeSheetStateSelector("clickedRow")

// what column is clicked in the sheet
export const selectClickedColumn = makeSheetStateSelector("clickedColumn")

export const selectFocusedColumnIndex = makeSheetStateSelector("focusedColumnIndex")

export const selectSortingColumnIndex = makeSheetStateSelector("sortingColumnIndex")

export const selectDefaultSortingColumnIndex = makeSheetStateSelector("publicSorting")

export const selectSearchingColumnIndexes = makeSheetStateSelector("searchingColumnIndexes", Immutable.fromJS([]))

export const selectSearchingColumnValues = makeSheetStateSelector("searchingColumnValues")

export const selectRowData = makeSheetStateSelector("row_data")

export const selectRowCount = makeSheetStateSelector("row_count")

export const selectSortType = makeSheetStateSelector("sortType")

export const selectVisualizationKeys = makeSheetStateSelector("visualization")

export const selectIsDownloading = makeSheetStateSelector("isDownloading")

export const selectSelectOpen = makeSheetStateSelector("selectOpen")

export const selectIsSubmitting = makeSheetStateSelector("isSubmitting")

export const selectResourceTableConfiguration = makeSheetStateSelector("resourceTableConfiguration")

export const selectMapFilterValues = makeSheetStateSelector("mapFilterValues")

export const selectSheetRowsTotalCount = makeSheetStateSelector("count")

// are we currently rendering a public sheet at the url /spreadsheet/external/[publicSlug]
// this selector is used
// it is not possible to view a public sheet if the is_public boolean is not true
export const selectIsPublicSheetMode = makeSheetStateSelector("isPublicSheetMode")

export const selectSafedSearchFilters = makeSheetStateSelector("safedSearchFilters")

export const selectCreatedRegions = makeSheetStateSelector("created_regions")

export const selectAddedIds = makeSheetStateSelector("added_ids")

export const selectDeletedIds = makeSheetStateSelector("deleted_ids")

export const selectShouldDisplayMathBarFromProps = makeSheetStateSelector("shouldDisplayMathBar")

export const selectIsLoadingMathBarFromState = (state) => state.sheet.get("isLoadingMathBar")

// -------------------- END UI state selectors ------------ //

export const selectIsResourceTable = makeSheetPropSelector("is_resource_table")

export const selectShouldDisplayMathBar = createSelector(
    selectShouldDisplayMathBarFromProps,
    selectIsResourceTable,
    (shouldDisplayMathBar, isResourceTable) => !isResourceTable && shouldDisplayMathBar,
)

// selects a key in the resourceTableConfiguration key under a particular sheet
// can be used to get resource table columns as well as other configuration passed to the ResourceTableContainer
/**
 * get the desired field from the resocuretableconfig
 * @param {Map} resourceTableConfig - a selection of props passed to ResourceTableContainer
 * @returns {String|Integer|Map} - the desired field, could be Immutable
 */
export const makeResourceTableConfigSelector = (key) =>
    createSelector(selectSheetStateWithId, (state) =>
        state ? state.getIn(["resourceTableConfiguration", key]) : undefined,
    )

export const selectColumns = createSelector(
    makeSheetStateSelector("columns"),
    (state, props) => makeResourceTableConfigSelector("columns")(state, props),
    selectIsResourceTable,
    (columns, resourceTableColumns, isResourceTable) => {
        if (resourceTableColumns && !resourceTableColumns.size) {
            console.warn("missing columns")
        } else if (isResourceTable) {
            return resourceTableColumns
        }
        return columns || Immutable.fromJS([])
    },
)

export const selectStoredSheetDataType = makeSheetPropSelector("data_type")

export const selectSheetDataType = createSelector(
    selectDataTypeFromProps,
    selectStoredSheetDataType,
    (propsDataType, storedDataType) => propsDataType || storedDataType,
)

// get the columns to render for Sheet.jsx
// handle the logic for hiding columns on public sheets in this selector
export const selectColumnsToRender = createSelector(
    selectColumns,
    selectIsPublicSheetMode,
    selectIsResourceTable,
    (columns, isPublicSheetMode, isResourceTable) => {
        if (isPublicSheetMode) {
            return columns
                .map((col, trueColumnIndex) => ({
                    ...getFieldsFromColumnObject(col, isResourceTable),
                    trueColumnIndex,
                }))
                .filter((col) => !col.should_hide_column_on_public)
                .toJS()
        } else {
            return columns
                .map((col, trueColumnIndex) => ({
                    ...getFieldsFromColumnObject(col, isResourceTable),
                    trueColumnIndex,
                }))
                .toJS()
        }
    },
)

export const selectSheetName = makeSheetPropSelector("name")

export const selectSheetCanFitInMemory = makeSheetPropSelector("can_fit_in_memory")

export const selectShowMap = makeSheetPropSelector("show_map")

export const selectPasswordRequired = makeSheetPropSelector("passwordRequired")

export const selectIsLocked = makeSheetPropSelector("is_locked")

export const selectRowHeightFactor = makeSheetPropSelector("row_height_factor")

export const selectRowHeight = createSelector(
    selectRowHeightFactor,
    selectIsResourceTable,
    (rowHeightFactor, isResourceTable) => {
        if (isResourceTable) {
            return sheetConstants.RESOURCE_TABLE_ROW_HEIGHT
        }
        if (!rowHeightFactor) {
            return sheetConstants.ROW_HEIGHT
        }
        return rowHeightFactor * sheetConstants.ROW_HEIGHT
    },
)

export const selectDatumColumnWidth = makeSheetPropSelector("datum_column_width")

export const selectOrganizationLogo = makeSheetPropSelector("organization_logo")

export const selectUseOrganizationDesign = makeSheetPropSelector("use_organization_design")

export const selectAllowPublicSheetReorderColumns = makeSheetPropSelector("allow_public_sheet_reorder_columns")

const selectSheetCreatorsFirstName = makeSheetPropSelector("first_name")
const selectSheetCreatorsLastName = makeSheetPropSelector("last_name")

const selectSheetCreatorsName = createSelector(
    selectSheetCreatorsFirstName,
    selectSheetCreatorsLastName,
    (firstName, lastName) => (firstName || lastName ? `${firstName || ""} ${lastName || ""}` : undefined),
)

export const selectDefaultSortColumnIndex = createSelector(selectColumns, (columns) => {
    if (columns) {
        const sortedColumnIndex = columns.findIndex(
            (column) =>
                column.get("default_sort_type") !== DjangIO.app.userdata.spreadsheet.types.SheetSortType.none.value,
        )

        // findIndex will return -1 if not found, but for us a column index of -1 is the datum column index
        if (sortedColumnIndex > -1) {
            return sortedColumnIndex
        }
    }
})

export const selectDefaultSortColumnType = createSelector(
    selectColumns,
    selectDefaultSortColumnIndex,
    (columns, defaultSortColumnIndex) => {
        if (columns && (defaultSortColumnIndex || defaultSortColumnIndex === 0)) {
            return columns.get(defaultSortColumnIndex).get("default_sort_type")
        }
    },
)

export const selectShouldShowDescription = makeSheetPropSelector("should_show_description")

export const selectDescription = makeSheetPropSelector("description")

export const showDatumColumn = makeSheetPropSelector("show_datum_column")

// this variable indicates that this sheet is publicly accessible
export const selectIsPublicSheet = makeSheetPropSelector("is_public")

// used to select the organization logo from the PublicSpreadsheetsApp component
// in public sheets
export const selectNavBarOrganizationLogo = createSelector(
    selectSheetKey,
    (state) => state,
    (sheetSlug, state) => selectOrganizationLogo(state, { id: sheetSlug }),
)

export const selectColumnCount = (state, ownProps) => {
    const sheetId = ownProps.sheetId ? ownProps.sheetId : ownProps.params.sheetId
    const id = createSheetKey(sheetId)
    const columns = selectColumns(state, { id })
    return columns && columns.size
}

export const selectSheetNameForHeader = (state, ownProps) => {
    const sheetId = ownProps.sheetId ? ownProps.sheetId : ownProps.params.sheetId
    const id = createSheetKey(sheetId)
    return selectSheetName(state, { id })
}

// need to have a spearate selector for the public nav bar since the param
// is not sheetId, but  sheet_slug
export const selectSheetNameForPublicNavBar = (state, ownProps) => {
    const sheetId = ownProps.sheetId ? ownProps.sheetId : ownProps.params.sheetId
    const id = createSheetKey(sheetId)
    return selectSheetName(state, { id })
}

export const selectSortingColumn = createSelector(
    selectSheetStateWithId,
    selectSortingColumnIndex,
    (sheet, columnIndex) =>
        columnIndex === undefined ? undefined : sheet.get("columns", Immutable.fromJS([])).get(columnIndex),
)

export const selectSortingColumnId = createSelector(
    selectSortingColumnIndex,
    selectSortingColumn,
    (columnIndex, column) =>
        columnIndex === sheetConstants.DATUM_COLUMN_INDEX
            ? sheetConstants.DATUM_COLUMN_INDEX
            : column
              ? column.get("id")
              : undefined,
)

export const selectDefaultSortingColumn = createSelector(
    selectSheetStateWithId,
    selectDefaultSortColumnIndex,
    (sheet, columnIndex) =>
        columnIndex === 0 || columnIndex ? sheet.get("columns", Immutable.fromJS([])).get(columnIndex) : undefined,
)

export const selectSearchingColumns = createSelector(
    selectSheetStateWithId,
    selectSearchingColumnIndexes,
    (sheet, columnIndexes) => {
        if (!columnIndexes) {
            return {}
        }
        const searchingColumns = {}
        sheet.get("columns", Immutable.fromJS([])).map((each, index) => {
            if (columnIndexes.includes(index)) {
                searchingColumns[index] = each
            }
        })
        return searchingColumns
    },
)

export const selectSearchingColumnValuesForBigSheetsAndDownloads = createSelector(
    selectSearchingColumns,
    selectSearchingColumnIndexes,
    selectSearchingColumnValues,
    (columns, indexes, values) => {
        const searchingValues = {}
        indexes = indexes || Immutable.fromJS([])
        indexes.forEach((index) => {
            if (index === sheetConstants.DATUM_COLUMN_INDEX) {
                searchingValues["-1"] = values.get(String(index))
            } else {
                searchingValues[columns[index].get("id")] = values.get(String(index))
            }
        })
        return searchingValues
    },
)

export const selectIsFilteredOrSorted = createSelector(
    selectSearchingColumnIndexes,
    selectSortingColumnIndex,
    (searchingColumnIndexes, sortingColumnIndex) =>
        (searchingColumnIndexes && searchingColumnIndexes.size !== 0) || sortingColumnIndex != null,
)

/**
 * This selector is responsible for return the data for the sheet
 * There are multiple steps invovled in getting the data formatted appropriately
 *     - first, if the sheet is a resource table or can't fit in memory, we don't want
 *     to filter client side at all. The rowData passed to this selector SHOULD
 *     be the final data for the sheet
 *
 * @param {() => Map} selectSheetStateWithId - the entire sheet Immutable map
 * @param {() => Object} selectFieldFromProps - the object representing this DjangIO field
 *
 * @returns Todo
 */
export const selectFilteredAndSortedRowData = createSelector(
    selectSheetStateWithId,
    selectRowData,
    selectSearchingColumns,
    selectSearchingColumnIndexes,
    selectSearchingColumnValues,
    selectSortingColumn,
    selectSortType,
    selectSheetCanFitInMemory,
    selectIsResourceTable,
    selectDefaultSortColumnType,
    selectDefaultSortingColumn,
    selectIsPublicSheetMode,
    selectMapFilterValues,
    selectSheetDataType,
    (
        sheet,
        rowData,
        searchingColumns,
        searchingColumnIndexes,
        searchingColumnValues,
        sortingColumn,
        sortType,
        canFitInMemory,
        isResourceTable,
        defaultSortColumnType,
        defaultSortingColumn,
        isPublicSheetMode,
        mapFilterValues,
        dataType,
    ) => {
        // if this sheet can't fit in memory
        if (!sheet || !canFitInMemory || isResourceTable) {
            // we filter on the backend, so just return the data
            return rowData
        }

        if (searchingColumnIndexes && searchingColumnIndexes.size) {
            searchingColumnIndexes.forEach((index) => {
                let searchingColumnValue = searchingColumnValues.get(String(index))
                let searchingColumn = searchingColumns[String(index)]

                let noFilterConditions = [
                    searchingColumnValue === undefined,
                    searchingColumnValue === null,
                    searchingColumnValue === "",
                    searchingColumnValue && searchingColumnValue.size === 0,
                ]
                if (!noFilterConditions.any()) {
                    // if we're searching the index
                    if (index === sheetConstants.DATUM_COLUMN_INDEX) {
                        const key = switches.rowSearchKeyForDataType(sheet.getIn(["sheet", "data_type"]))

                        // key should be a string, so we can safely check for truthiness this way
                        rowData = rowData.filter(
                            (row) =>
                                row.get(key) &&
                                row.get(key, "").toLowerCase().includes(String(searchingColumnValue).toLowerCase()),
                        )
                    } else {
                        const tagType = searchingColumn.getIn(["custom_tag", "tag_type"])
                        const filter = switches.filterFunctionForColumnDataType(
                            searchingColumnValue,
                            searchingColumn.get("column_data_type"),
                            searchingColumn.get("column_type"),
                            searchingColumn.get("field"),
                            sheet.getIn(["sheet", "data_type"]),
                            tagType,
                        )
                        const filterColumnData = switches.computeColumnData(searchingColumn, sheet.get("columns"))
                        rowData = rowData.filter((row) => filter(filterColumnData.get(String(row.get("id")))))
                    }
                }
            })
        }

        const sortRows = (column, sortHow) => {
            // determine if we should invert the sorting order, derived from backend
            const columnType = DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(
                column.get("column_type"),
            )
            const shouldInvert = columnType && columnType.invert_sorting
            const tagType = column.getIn(["custom_tag", "tag_type"])

            const sort = switches.sortFunctionForColumnDataType(
                shouldInvert ? reverseSortDirection(sortHow) : sortHow,
                column.get("column_data_type"),
                column.get("column_type"),
                sheet.getIn(["sheet", "data_type"]),
                column.get("field"), // if its an enum, we want to be able to turn on alphabetical sorting
                tagType,
            )
            const columnData = switches.computeColumnData(column, sheet.get("columns"))
            rowData = rowData.sort((row1, row2) => {
                // get the value of both rows in the column
                const row1Value = columnData && columnData.get(String(row1.get("id")))
                const row2Value = columnData && columnData.get(String(row2.get("id")))
                if (!columnData) {
                    const columnDataArgs = {
                        column,
                        sheetColumns: sheet.get("columns"),
                    }
                    console.warn(columnDataArgs)
                    Sentry.captureException(new Error("Sheet could not compute column data"), {
                        contexts: columnDataArgs,
                    })
                }
                return sort(row1Value, row2Value)
            })
        }

        const sortConditions = [sortingColumn, sortType]

        if (sortConditions.all()) {
            sortRows(sortingColumn, sortType)
        } else if (defaultSortingColumn && !isPublicSheetMode) {
            sortRows(defaultSortingColumn, defaultSortColumnType)
        }

        if (isPublicSheetMode) {
            // handle any filters the map may have added
            // if the user hasn't selected any states, default to the reverse logic of below and show all rows
            if (!mapFilterValues || !mapFilterValues.size) {
                return rowData
            } else {
                // if the sheet is a person sheet, the backend sends the region data
                // in the key "most_recent_state__region"
                const regionKey =
                    dataType === DjangIO.app.models.QuorumDataType.person.value ? "most_recent_state__region" : "region"
                // if the region is in one of the chosen mapFilterValues, it passes the check
                return rowData.filter((row) => mapFilterValues.includes(row.get(regionKey)))
            }
        }
        return rowData
    },
)

/**
 * get only the ids of the filtered rows to support sheet downloads
 * passing the row ids to the backend allows the sheet download to replicate
 * the filtering / sorting on the frontend exactly
 * @param {Map} rowData - a Map of the rowData for the sheet
 * @returns {Array[Integer]} - array of row ids
 * @type {(state: any, props: any) => any}
 */
const selectFilteredAndSortedRowIds = createSelector(
    selectFilteredAndSortedRowData,
    (rowData) => rowData && rowData.map((row) => row.get("id")).toJS(),
)

// extracts the column id from the props of a stat cell
/** @type {(state: any, props: any) => any} */
export const selectColumnIdFromProps = (state, { columnId }) => columnId
/** @type {(state: any, props: any) => any} */
export const selectColumnIndexFromProps = (state, { columnIndex }) => columnIndex

/** @type {(state: any, props: any) => any} */
export const selectColumnForCell = createSelector(selectColumns, selectColumnIndexFromProps, (columns, columnIndex) =>
    columns.get(columnIndex),
)

/** @type {(state: any, props: any) => any} */
export const selectOptionsForCustomTagCell = createSelector(selectColumnForCell, (column) =>
    column.getIn(["custom_tag", "options_new"]),
)

/** @type {(state: any, props: any) => any} */
export const selectTagTypeForCustomTagCell = createSelector(
    selectColumnForCell,
    (column) => column && column.getIn(["custom_tag", "tag_type"]),
)

/** @type {(state: any, props: any) => any} */
export const selectIsEditableForCustomTagCell = createSelector(
    selectColumnForCell,
    selectIsPublicSheetMode,
    (column, isPublicSheetMode) => column.getIn(["custom_tag", "is_editable"]) && !isPublicSheetMode,
)

// ---------- BEGIN SheetForm selectors ----------------- //
/** @type {(state: any, props: any) => any} */
export const selectSheetRows = makeSheetPropSelector("rows")

/** @type {(state: any, props: any) => any} */
const selectLimitedToTeams = createSelector(
    makeSheetPropSelector("limited_to_teams"),
    (limitedToTeams) => limitedToTeams || Immutable.fromJS([]),
)

// Return an array of Team URIS for the SheetForm
/** @type {(state: any, props: any) => any} */
export const selectLimitedToTeamsForSheetForm = createSelector(selectLimitedToTeams, (limitedToTeamsList) =>
    limitedToTeamsList.toJS(),
)

// these are not camelcased because it is convenient to send the entire sheet object
// to the backend without manipulating the fields
/** @type {(state: any, props: any) => any} */
export const sheetFormSheetSelector = createStructuredSelector({
    allow_public_sheet_reorder_columns: selectAllowPublicSheetReorderColumns,
    columns: makeSheetPropSelector("columns"),
    creatorsName: selectSheetCreatorsName,
    data_type: makeSheetPropSelector("data_type"),
    description: selectDescription,
    is_locked: selectIsLocked,
    is_public: makeSheetPropSelector("is_public"),
    public_password: makeSheetPropSelector("public_password"),
    external_regions: makeSheetPropSelector("external_regions"),
    external_custom_region_selection: makeSheetPropSelector("external_custom_region_selection"),
    limited_to_teams: makeSheetPropSelector("limited_to_teams"),
    is_limited_to_creator: makeSheetPropSelector("is_limited_to_creator"),
    name: makeSheetPropSelector("name"),
    organization_design: makeSheetPropSelector("organization_design"),
    organization: makeSheetPropSelector("organization"), // an organization URI
    projects: makeSheetPropSelector("projects"),
    public_slug: makeSheetPropSelector("public_slug"),
    should_show_description: selectShouldShowDescription,
    show_map: selectShowMap,
    use_organization_design: selectUseOrganizationDesign,
})

// we hide the map if the data-type is not bill, person, or regulation, or if the sheet can't fit
// in memory
/** @type {(state: any, props: any) => any} */
export const selectHideMapButton = createSelector(
    sheetFormSheetSelector,
    makeSheetPropSelector("can_fit_in_memory"),
    (sheet, canFitInMemory) =>
        !(
            sheet.data_type === DjangIO.app.models.QuorumDataType.bill.value ||
            sheet.data_type === DjangIO.app.models.QuorumDataType.person.value ||
            sheet.data_type === DjangIO.app.models.QuorumDataType.supporter.value ||
            sheet.data_type === DjangIO.app.models.QuorumDataType.regulation.value
        ) || !canFitInMemory,
)

// ---------- END SheetForm selectors ----------------- //

// --------- BEGIN  ResourceTable selectors ----------------- //
/** @type {(state: any, props: any) => any} */
export const selectRowFilterFromProps = (state, { rowFilter = {} }) => rowFilter
/** @type {(state: any, props: any) => any} */
export const selectRowResourceFromProps = (state, { rowResource }) => rowResource

/**
 * selectColumnSortingQuerymethod
 * @name someName
 * @function
 * @param {Array} columns - array of column configurations for a resource table
 * @param {Number} sortingColumnIndex - index of column being sorting
 * @param {String} datumColumnSortingQuerymethod - name of querymethod that is used to sort the datum column
 * @returns {String} - querymethod that should be used to sort the column currently being sorted, may be undefined
 * @type {(state: any, props: any) => any}
 * NOTE: This is only used for resource tables
 */
export const selectColumnSortingQuerymethod = createSelector(
    selectColumnsFromProps,
    selectSortingColumnIndex,
    makeResourceTableConfigSelector("datumColumnSortingQuerymethod"),
    (columns, sortingColumnIndex, datumColumnSortingQuerymethod) => {
        if (sortingColumnIndex !== 0 && !sortingColumnIndex) {
            return undefined
        } else if (sortingColumnIndex === sheetConstants.DATUM_COLUMN_INDEX) {
            return datumColumnSortingQuerymethod
        }
        const sortingColumn = columns.get(sortingColumnIndex)

        // developers can override the default sorting functionality to use a querymethod to apply ordering
        // this will be passed as a filter to the resource table
        return sortingColumn.get("columnSortingQuerymethod")
    },
)

// this selector is responsible for building the filters for a ResourceTable's request
/** @type {(state: any, props: any) => any} */
export const selectResourceTableRowFilter = createSelector(
    selectColumnsFromProps,
    selectRowFilterFromProps,
    selectRowResourceFromProps,
    selectSearchingColumnIndexes,
    selectSearchingColumnValues,
    (_, props) => props.datumFilterOn,
    selectColumnSortingQuerymethod,
    selectSortType,

    (
        columns,
        rowFilter,
        rowResource,
        columnIndexes,
        columnValues,
        datumFilterOn,
        columnSortingQuerymethod,
        sortType,
    ) => {
        columnIndexes = columnIndexes || Immutable.fromJS([])
        columnIndexes.forEach((columnIndex) => {
            const columnValue = columnValues.toJS()[columnIndex]
            const conditions = [
                // if the searching column is defined
                columnIndex !== null && columnIndex !== undefined,
                // if the searching value is not null
                columnValue !== null && columnValue !== undefined,
                // if the searching value is not empty
                columnValue !== "",
                // account for columnValue being 'false'
                !columnValue || (columnValue && columnValue.size !== 0),
            ]
            if (conditions.all()) {
                // if searching by -1
                if (columnIndex === sheetConstants.DATUM_COLUMN_INDEX) {
                    // If searching the main data column, we use advanced search
                    // columnValue is a string in this case, so it does not need to parsed
                    rowFilter = Object.assign({}, rowFilter, { [datumFilterOn || "advanced_search"]: columnValue })
                } else {
                    const searchingField =
                        columns.getIn([columnIndex, "resourceTableFilterOn"]) || columns.getIn([columnIndex, "field"])

                    // if we were passed one - this is a scenario when we'd like to support choosing a filter
                    const overrideFilterKey = columns.getIn([columnIndex, "overrideFilterKey"])

                    const resourceField = rowResource._meta.get_field(searchingField) // eslint-disable-line no-underscore-dangle

                    // if the field is a foreign key, we have to get the desired field on the related model
                    // this is specified in the column configuration
                    // otherwise, default to the field
                    const searchKey = columns.getIn([columnIndex, "foreignKeyField"])
                        ? `${searchingField}__${columns.getIn([columnIndex, "foreignKeyField"])}`
                        : searchingField

                    const filter = switches.filterForResourceField(
                        resourceField,
                        searchKey,
                        columnValue,
                        overrideFilterKey,
                    )

                    rowFilter = Object.assign({}, rowFilter, filter)
                }
            }
        })
        if (columnSortingQuerymethod) {
            rowFilter = {
                ...rowFilter,
                [columnSortingQuerymethod]: sortType,
            }
        }
        return rowFilter
    },
)

/** @type {(state: any, props: any) => any} */
export const selectResourceTableSortKey = createSelector(
    selectColumnsFromProps,
    selectSortingColumnIndex,
    selectSortType,
    selectColumnSortingQuerymethod,
    (columns, sortingColumnIndex, sortType, columnSortingQuerymethod) => {
        if (
            sortingColumnIndex === sheetConstants.DATUM_COLUMN_INDEX ||
            (sortingColumnIndex !== 0 && !sortingColumnIndex) ||
            // if we are using a querymethod to sort, we don't want/need a sortKey
            columnSortingQuerymethod
        ) {
            return undefined
        }
        const sortingColumn = columns.get(sortingColumnIndex)

        const shouldInvert =
            // if the column wants to invert its sorting
            sortingColumn.get("invert_sorting", false)
                ? // then invert when it's descending
                  sortType === sortTypes.DESC
                : // else invert when its ascending
                  sortType === sortTypes.ASC

        // developers can override the default sort key, which is typically the field itself
        const sortingField = sortingColumn.get("resourceTableSortOn") || sortingColumn.get("field")

        // prevent Immutable from being serialized
        if (sortingField instanceof Immutable.List) {
            return undefined
        }

        return shouldInvert ? sortingField : `-${sortingField}`
    },
)

// --------- BEGIN  ResourceTable selectors ----------------- //
/** @type {(state: any, props: any) => any} */
export const selectNoResultsAndNoFilters = createSelector(
    selectRowData,
    selectIsFilteredOrSorted,
    (rowData, isFilteredOrSorted) => rowData && !rowData.size && !isFilteredOrSorted,
)

/** @type {(state: any, props: any) => any} */
export const selectNoResults = createSelector(
    selectFilteredAndSortedRowData,
    selectNoResultsAndNoFilters,
    (_, props) => props.shouldShowNoResultsWithoutFilters,
    (rowData, noResultsAndNoFilters, shouldShowNoResultsWithoutFilters) =>
        rowData && !rowData.size && (!noResultsAndNoFilters || shouldShowNoResultsWithoutFilters),
)
// ------------BEGIN Container sheet selectors ----------------- //
// these selectors are used to access properties from the store from container components,
// one level up from the Sheet component layer

/** @type {(state: any, props: any) => any} */
const selectContainerStateWithId = createSelector(sheetSelector, selectSheetKey, (sheet, sheetKey) =>
    sheet.get(sheetKey),
)

// selects the public sheet from the store
// state => sheet => sheet:randomSheetSlug
// key indexes into the sheet
const makeContainerStateSelector = (key) =>
    createSelector(selectContainerStateWithId, (state) => (state ? state.get(key) : undefined))

// whether we are making any API call (for example reloading the table)
/** @type {(state: any, props: any) => any} */
export const selectContainerSheetIsLoading = makeContainerStateSelector("loading")

/** @type {(state: any, props: any) => any} */
export const selectUserIdFromResourceUri = createSelector(makeSheetPropSelector("user"), (user) =>
    DjangIO.app.userdata.models.QuorumUser.idFromResourceUri(user),
)

// get the owner of the sheet
/** @type {(state: any, props: any) => any} */
export const selectIsSheetCreator = createSelector(
    selectUserId,
    selectUserIdFromResourceUri,
    // we know it is the creator IF 1) ids match, or 2) id does not exist aka it is new
    (userId, userIdFromResource) => (userIdFromResource && userId === userIdFromResource) || !userIdFromResource,
)

/** @type {(state: any, props: any) => any} */
export const selectSheetIsLockedForUser = createSelector(
    selectIsSheetCreator,
    selectIsLocked,
    (isCreator, isLocked) => isLocked && !isCreator,
)

// helper selector for locked sheets to determine who can remove columns
/** @type {(state: any, props: any) => any} */
export const selectCanRemoveColumns = createSelector(
    selectSheetIsLockedForUser,
    selectIsPublicSheetMode,
    selectIsResourceTable,
    (isLockedForUser, isPublicSheetMode, isResourceTable) => !(isLockedForUser || isPublicSheetMode || isResourceTable),
)

// only allow users to hide columns if the sheet is not locked for the user (ie the sheet
// is unlocked, or locked but is being viewed by the owner), and the sheet is public
// this will hide the button if the sheet isn't public
/** @type {(state: any, props: any) => any} */
export const selectCanHideColumns = createSelector(
    selectSheetIsLockedForUser,
    makeSheetPropSelector("is_public"),
    selectIsPublicSheetMode,
    (isLockedForUser, isPublic, isPublicSheetMode) => !isLockedForUser && isPublic && !isPublicSheetMode,
)

// determines if any columns on this sheet can have custom colorings displayed when rendered
/** @type {(state: any, props: any) => any} */
export const selectCanCustomColorColumns = createSelector(selectIsResourceTable, (isResourceTable) => !isResourceTable)

/** @type {(state: any, props: any) => any} */
export const selectCanSetDefaultSort = createSelector(
    selectSheetIsLockedForUser,
    selectIsPublicSheetMode,
    selectIsResourceTable,
    (isLockedForUser, isPublicSheetMode, isResourceTable) => !(isLockedForUser || isPublicSheetMode || isResourceTable),
)

// determines whether to show spinner when initially loading a sheet
// don't want to show the spinner for requests that occur after the sheet loads for the first time
/** @type {(state: any, props: any) => any} */
export const selectSheetRequestLoadingForContainer = createSelector(
    selectInitialSheetRequestLoading,
    selectSheetIsLoading,
    (initiallyLoading, isLoading) => initiallyLoading && isLoading,
)

// determine whether we should show the loading spinner for ResourceTables
// sheetInitiallyLoading will only be true for the first request
// shouldShowLoadingSpinnerInitially allows for turning off showing the loading spinner initial
// resourceTableShowLoadingSpinner allows a developer to override sheets default behavior and take control of loading
// sheetsShowLoadingSpinner allows the Sheets infrastructure to enable showing the loading spinner
/** @type {(state: any, props: any) => any} */
export const selectShouldShowLoadingSpinner = createSelector(
    selectSheetRequestLoadingForContainer,
    (_, props) => props.shouldShowLoadingSpinnerInitially,
    (_, props) => props.resourceTableShowLoadingSpinner,
    selectShouldShowLoadingSpinnerOverride,
    (
        sheetInitiallyLoading,
        shouldShowLoadingSpinnerInitially,
        resourceTableShowLoadingSpinner,
        shouldShowLoadingSpinnerOverride,
    ) => {
        if (resourceTableShowLoadingSpinner || shouldShowLoadingSpinnerOverride) {
            return true
        }
        return sheetInitiallyLoading && shouldShowLoadingSpinnerInitially
    },
)

// ------------END container sheet selectors ----------------- //

// ------------ BEGIN public sheet selectors ----------------- //

// we need these selectors because although we are pulling from the same location as makeSheetStateSelector,
// these are providing data to a different component (a top level Container component),
// and they don't work when reused on PublicSheetContainer
/** @type {(state: any, props: any) => any} */
export const selectPublicSheetSearchingColumnValues = makeContainerStateSelector("searchingColumnValues")

/** @type {(state: any, props: any) => any} */
export const selectPublicSheetIsDownloading = makeContainerStateSelector("isDownloading")

// should the public sheet display a map
// disabled for small screen sizes
/** @type {(state: any, props: any) => any} */
const selectPublicSheetShowMap = createSelector(
    selectSheetCanFitInMemory,
    selectShowMap,
    selectWindowWidth,
    selectWindowHeight, //                                                              takes care of when width changes,                 takes care of initial width
    (isSmallSheet, show_map, windowWidth, windowHeight) =>
        isSmallSheet &&
        show_map &&
        (windowWidth > sheetsVariables.MAX_MOBILE_WIDTH || window.innerWidth > sheetsVariables.MAX_MOBILE_WIDTH),
)

// get the region data for each row in the sheet, filtered by the users' filters
// a separate selector because this is for a container
// if the sheet is a person sheet, the backend sends the region data
// in the key "most_recent_state__region"
/** @type {(state: any, props: any) => any} */
const selectFilteredMapRegionValues = createSelector(
    selectFilteredAndSortedRowData,
    selectSheetDataType,
    (filteredRowData, dataType) => {
        if (filteredRowData) {
            const regionKey =
                dataType === DjangIO.app.models.QuorumDataType.person.value ? "most_recent_state__region" : "region"
            return filteredRowData.map((row) => row.get(regionKey))
        } else {
            return undefined
        }
    },
)

// compute an object mapping of region key => row count in region from map_region_values provided by the
// api response
/** @type {(state: any, props: any) => any} */
const selectRegionDataForMap = createSelector(selectFilteredMapRegionValues, (filteredMapRegionValues) =>
    filteredMapRegionValues ? switches.generateMapData(filteredMapRegionValues) : undefined,
)

// Get the text to be shown in the search bar
// people should have a falsey searchName
/** @type {(state: any, props: any) => any} */
export const selectSearchBarPlaceHolder = createSelector(
    selectSheetRequestLoadingForContainer,
    (state, props) => selectSheetDataType(state, props),
    (isInitiallyLoading, dataType) => {
        if (isInitiallyLoading) {
            return `Loading...`
        }
        if (dataType) {
            const searchKey = DjangIO.app.userdata.spreadsheet.types.SheetDataType.by_value(dataType).datum_search_key
            const searchName =
                DjangIO.app.userdata.spreadsheet.types.SheetDataType.by_value(dataType).plural.toLowerCase()
            if (searchName && !DjangIO.app.userdata.spreadsheet.types.SheetDataType.person === dataType) {
                return `Search ${searchName} by ${searchKey ? searchKey : "name"}...`
            } else {
                return `Search by ${searchKey ? searchKey : "name"}...`
            }
        }
        return "Search..."
    },
)
// ------------- END public sheet selectrors ----------------- //

// BEGIN cellState selectors

/** @type {(state: any, props: any) => any} */
export const selectCellStates = makeSheetStateSelector("cellStates")
/** @type {(state: any, props: any) => any} */
export const selectCellStateToStyleProp = makeResourceTableConfigSelector("cellStateStyleMap")

// ------------- BEGIN Sheet.jsx selectors ------------------- //
// Disable reordering for resource tables
// Disable reordering for public sheets, unless we are allowed to reorder columns
/** @type {(state: any, props: any) => any} */
export const selectIsReorderable = createSelector(
    selectCanRemoveColumns,
    selectIsPublicSheetMode,
    selectAllowPublicSheetReorderColumns,
    (canRemoveColumns, isPublicSheetMode, allowPublicSheetReorder) =>
        canRemoveColumns || (isPublicSheetMode && allowPublicSheetReorder),
)

/** @type {(state: any, props: any) => any} */
export const selectDownloadArguments = createSelector(
    selectSearchingColumnValuesForBigSheetsAndDownloads,
    selectSortingColumnId,
    selectSortType,
    selectSheetCanFitInMemory,
    selectFilteredAndSortedRowIds,
    (searchingColumnValues, sortingColumnId, sortType, shouldLoadFullSheetIntoMemory, filteredAndSortedRowIds) => {
        const downloadArguments = {}
        // we only want to send values if they are truthy
        if (searchingColumnValues) {
            downloadArguments.searchingValue = JSON.stringify(searchingColumnValues)
        }
        if (sortingColumnId) {
            downloadArguments.sortingId = sortingColumnId
        }
        if (sortType) {
            downloadArguments.sortType = sortType
        }

        const anyFilteringConditions = [searchingColumnValues, sortingColumnId]

        // if we are a small sheet and have any filtering/sorting, also send the row ids to the backend
        // these will be used instead of the above filtering/sorting conditions
        // if available
        if (filteredAndSortedRowIds && shouldLoadFullSheetIntoMemory && anyFilteringConditions.any()) {
            // if the length of stringifiedIds exceeds the maximum query string, we can't send it
            // Bypassing this limit can generate a 414 error code, URL length limit bypassed
            // TODO: Detail exact limitation for production as some local environments do not have the same limit
            const stringifiedIds = JSON.stringify(filteredAndSortedRowIds)

            // commas get sent as %2C, so account for that with the 2x
            if (stringifiedIds.length + 2 * filteredAndSortedRowIds.length < 4000) {
                downloadArguments.filteredAndSortedRowIds = stringifiedIds
            }
        }
        return downloadArguments
    },
)

// select how far user needs to scroll before we load the next interval of results in the table
// if there are fewer than 50 rows in the sheet, a user with a large display may not be able to reach
// TABLE_SCROLL_THRESHOLD (currently 0.55) because they can see more than 55% of the rows without scrolling
/** @type {(state: any, props: any) => any} */
export const selectTableScrollThreshold = createSelector(
    (_, props) => props.rowData,
    (rowData) =>
        rowData && rowData.size < 50
            ? sheetConstants.TABLE_FEW_ROWS_SCROLL_THRESHOLD
            : sheetConstants.TABLE_SCROLL_THRESHOLD,
)

// ------------- BEGIN structured selectors ------------------ //
/** @type {(state: any, props: any) => any} */
export const smallSheetConnection = createStructuredSelector({
    clickedColumn: selectClickedColumn,
    clickedRow: selectClickedRow,
    columns: selectColumns,
    dataType: selectSheetDataType,
    datumColumnWidth: selectDatumColumnWidth,
    focusedColumnIndex: selectFocusedColumnIndex,
    isFilteredOrSorted: selectIsFilteredOrSorted,
    isSelectOpen: selectSelectOpen,
    loading: selectSheetIsLoading,
    name: selectSheetName,
    noResults: selectNoResults,
    rowData: selectFilteredAndSortedRowData,
    rowCount: selectRowCount,
    rowHeight: selectRowHeight,
    rowHeightFactor: selectRowHeightFactor,
    rows: selectSheetRows,
    searchingColumnIndexes: selectSearchingColumnIndexes,
    showDatumColumn,
    sortingColumnIndex: selectSortingColumnIndex,
    sortType: selectSortType,
})

/** @type {(state: any, props: any) => any} */
export const bigSheetConnection = createStructuredSelector({
    // regular sheet props
    name: selectSheetName,
    columns: selectColumns,
    clickedRow: selectClickedRow,
    clickedColumn: selectClickedColumn,
    rowData: selectFilteredAndSortedRowData,
    loading: selectSheetIsLoading,
    isLoadingBigSheetData: selectIsLoadingBigSheetData,
    dataType: selectSheetDataType,
    datumColumnWidth: selectDatumColumnWidth,
    sortingColumnIndex: selectSortingColumnIndex,
    focusedColumnIndex: selectFocusedColumnIndex,
    searchingColumnIndexes: selectSearchingColumnIndexes,
    isFilteredOrSorted: selectIsFilteredOrSorted,
    noResults: selectNoResults,
    rowHeight: selectRowHeight,
    rowHeightFactor: selectRowHeightFactor,
    rows: selectSheetRows,
    rowCount: selectRowCount,
    sortType: selectSortType,
    isSelectOpen: selectSelectOpen,
    showDatumColumn,

    // here we are just going to pass dict: {columnId: value}
    searchingColumnValues: selectSearchingColumnValuesForBigSheetsAndDownloads,
    sortingColumnId: selectSortingColumnId,
})

/** @type {(state: any, props: any) => any} */
export const selectNameFromProps = (_, props) => props.name

// Select the associated QuorumDataType
/** @type {(state: any, props: any) => any} */
export const selectQuorumDataType = createSelector(selectDataTypeFromProps, (dataType) =>
    Object.values(DjangIO.app.models.QuorumDataType.items()).find((qdt) => qdt.value === dataType),
)

// Select the associated feature
/** @type {(state: any, props: any) => any} */
export const selectFeature = createSelector(selectNameFromProps, selectQuorumDataType, (name, qdt) => {
    const feature = qdt ? DjangIO.app.models.Feature.items().find((item) => item.value === qdt.feature) : undefined
    // Destructure feature into a new object to avoid mutating DjangIO's copy below
    const featureCopy = feature ? { ...feature } : undefined

    if (featureCopy) {
        // For tables with tabs, select the appropriate empty state message
        featureCopy.empty_state_title =
            typeof featureCopy.empty_state_title === "object"
                ? featureCopy.empty_state_title[name]
                : featureCopy.empty_state_title

        featureCopy.empty_state_paragraph =
            typeof featureCopy.empty_state_paragraph === "object"
                ? featureCopy.empty_state_paragraph[name]
                : featureCopy.empty_state_paragraph

        // Check for an empty state specific Icons (also supports per tab values)
        if (featureCopy.empty_state_icon) {
            featureCopy.icon =
                typeof featureCopy.empty_state_icon === "object"
                    ? featureCopy.empty_state_icon[name]
                    : featureCopy.empty_state_icon
        }

        if (featureCopy.empty_state_icon_family) {
            featureCopy.icon_family =
                typeof featureCopy.empty_state_icon_family === "object"
                    ? featureCopy.empty_state_icon_family[name]
                    : featureCopy.empty_state_icon_family
        }
    }
    return featureCopy
})

// Determine if we should render an empty state
/** @type {(state: any, props: any) => any} */
export const selectShouldShowEmptyState = createSelector(
    selectFeature,
    selectSuppressEmptyState,
    selectNoResultsAndNoFilters,
    (feature, suppressEmptyState, noResultsAndNoFilters) => {
        return (
            (!suppressEmptyState && noResultsAndNoFilters && Boolean(feature) && Boolean(feature.empty_state_title)) ||
            // The new Texting feature uses a generic message instead of a custom empty_state_title for each QDT
            (feature?.value === DjangIO.app.models.Feature.outbound_texting.value &&
                !suppressEmptyState &&
                noResultsAndNoFilters &&
                Boolean(feature))
        )
    },
)

/** @type {(state: any, props: any) => any} */
export const selectFormattedSafedSearchFilters = createSelector(selectSafedSearchFilters, (safedSearch) => {
    // picks the filters of interest out of the safedSearch if they exist
    // return the filters as is otherwise as not important

    let safedSearchFilters = safedSearch && safedSearch.getIn(["0", "for_searchify_filters", "filters"])
    safedSearchFilters = safedSearchFilters && safedSearchFilters.toJS()
    if (safedSearchFilters && safedSearchFilters["0"]) safedSearchFilters["0"]["for_outbox"] = true
    return safedSearchFilters
})
/**
 * @description - takes the added/removed ids from the redux store and converts them to JS if they exist.
 * @type {(state: any, props: any) => any}
 */
export const selectChosenIds = createSelector(selectAddedIds, selectDeletedIds, (addedIds, deletedIds) => ({
    added_ids: addedIds && addedIds.toJS(),
    deleted_ids: deletedIds && deletedIds.toJS(),
}))

/**
 * @description - Combines the basic ID and sheet name into a single object
 * @type {(state: any, props: any) => any}
 */
export const selectIdNameParams = createSelector(selectBasicSheetId, selectSheetName, (id, name) => ({ id, name }))

/**
 * @description - Constructs the email all params used for the segue to populate staffer/contact/official
 * selections in outbox
 * @type {(state: any, props: any) => any}
 */
export const selectCreateEmailAllParams = createSelector(
    selectIdNameParams,
    selectFormattedSafedSearchFilters,
    selectSheetDataType,
    selectChosenIds,
    selectCreatedRegions,
    (idNameParams, advSearchParams, personType, chosenIDs, created_regions) => {
        const hasOfficialOutbox = checkPermission(DjangIO.app.models.PermissionType.officials_outbox)
        const createEmailOptionProps = {
            ...idNameParams,
            ...chosenIDs,
            isNotReminder: true,
            advSearchParams,
            sheetDataType: personType,
            created_regions,
            addContactSelection:
                personType === DjangIO.app.models.QuorumDataType.supporter.value &&
                checkPermission(DjangIO.app.models.PermissionType.custom_contacts),
            addOfficialSelection: personType === DjangIO.app.models.QuorumDataType.person.value && hasOfficialOutbox,
            addStafferSelection: personType === DjangIO.app.models.QuorumDataType.staffer.value && hasOfficialOutbox,
        }

        return createEmailOptionProps
    },
)

/** @type {(state: any, props: any) => any} */
export const selectShouldShowEmailAllButton = createSelector(
    selectSheetDataType,
    selectSheetRequestLoadingForContainer,
    selectSheetIsLoading,
    (selectSheetDataType, refreshing, loading) =>
        (checkPermission(DjangIO.app.models.PermissionType.custom_outbox) ||
            checkPermission(DjangIO.app.models.PermissionType.officials_outbox)) &&
        !loading &&
        !refreshing &&
        sheetConstants.VALID_EMAIL_ALL_BUTTON_SHEET_DATA_TYPES.includes(selectSheetDataType),
)

/** @type {(state: any, props: any) => any} */
export const resourceTableConnection = createStructuredSelector({
    canFitInMemory: () => false, // resource tables dont have client side filtering/sorting
    clickedColumn: selectClickedColumn,
    clickedRow: selectClickedRow,
    columnSortingQuerymethod: selectColumnSortingQuerymethod,
    feature: selectFeature,
    focusedColumnIndex: selectFocusedColumnIndex,
    isFilteredOrSorted: selectIsFilteredOrSorted,
    loading: selectSheetIsLoading,
    noResults: selectNoResults,
    quorumDataType: selectQuorumDataType,
    rowData: selectFilteredAndSortedRowData,
    rowFilter: selectResourceTableRowFilter,
    searchingColumnIndexes: selectSearchingColumnIndexes,
    sheetInitiallyLoading: selectInitialSheetRequestLoading,
    shouldShowEmptyState: selectShouldShowEmptyState,
    shouldShowLoadingSpinner: selectShouldShowLoadingSpinner,
    sortingColumnIndex: selectSortingColumnIndex,
    sortKey: selectResourceTableSortKey,
    sortType: selectSortType,
})

/** @type {(state: any, props: any) => any} */
export const sheetContainerConnection = createStructuredSelector({
    canHideColumns: selectCanHideColumns,
    canRemoveColumns: selectCanRemoveColumns,
    columnCount: selectColumnCount,
    emailAllParams: selectCreateEmailAllParams,
    downloadArguments: selectDownloadArguments,
    filteredAndSortedRowIds: selectFilteredAndSortedRowIds,
    isCreator: selectIsSheetCreator,
    isDownloading: selectIsDownloading,
    isLoading: selectSheetIsLoading,
    isLoadingMathBar: selectIsLoadingMathBarFromState,
    isLocked: selectIsLocked,
    searchingColumnValues: selectSearchingColumnValuesForBigSheetsAndDownloads,
    sheetInitiallyLoading: selectSheetRequestLoadingForContainer,
    sheetKey: selectSheetKey,
    sheetIsLockedForUser: selectSheetIsLockedForUser,
    sheetName: selectSheetNameForHeader,
    sheetQDT: selectSheetDataType,
    shouldDisplayMathBar: selectShouldDisplayMathBar,
    shouldLoadFullSheetIntoMemory: selectSheetCanFitInMemory,
    shouldShowEmailAllButton: selectShouldShowEmailAllButton,
    sortingColumnId: selectSortingColumnId,
    sortingColumnIndex: selectSortingColumnIndex,
    sortType: selectSortType,
})

// public sheet container connection
/** @type {(state: any, props: any) => any} */
export const publicSheetContainerConnection = createStructuredSelector({
    organizationLogo: selectNavBarOrganizationLogo,
    sheetName: selectSheetNameForHeader,
    allow_public_sheet_reorder_columns: selectAllowPublicSheetReorderColumns,
    columnCount: selectColumnCount,
    dataType: selectSheetDataType,
    defaultPublicSortColumnIndex: selectDefaultSortColumnIndex,
    defaultPublicSortColumnType: selectDefaultSortColumnType,
    description: selectDescription,
    downloadArguments: selectDownloadArguments,
    id: selectSheetKey,
    isDownloading: selectPublicSheetIsDownloading,
    loading: selectSheetIsLoading,
    mapData: selectRegionDataForMap,
    mapFilterValues: selectMapFilterValues,
    searchBarPlaceHolder: selectSearchBarPlaceHolder,
    searchingColumnValues: selectSearchingColumnValues,
    searchingColumnIndexes: selectSearchingColumnIndexes,
    sheetInitiallyLoading: selectSheetRequestLoadingForContainer,
    shouldLoadFullSheetIntoMemory: selectSheetCanFitInMemory,
    shouldShowDescription: selectShouldShowDescription,
    show_map: selectPublicSheetShowMap,
    name: selectSheetName,
})

/** @type {(state: any, props: any) => any} */
const sheetNeedsInit = createSelector(selectSheetStateWithId, (sheet) => !sheet)

/** @type {(state: any, props: any) => any} */
export const selectVisualizationDisplayKey = createSelector(
    selectStoredSheetDataType,
    (dataType) => dataType && DjangIO.app.models.QuorumDataType.by_value(dataType).display_key,
)

/** @type {(state: any, props: any) => any} */
export const sheetAnalyticsConnection = createStructuredSelector({
    loading: selectSheetIsLoading,
    sheetDataType: selectSheetDataType,
    rows: selectRowData,
    columns: selectColumns,
    visualization: selectVisualizationKeys,
    sheetName: selectSheetName,
    canFitInMemory: selectSheetCanFitInMemory,
    displayKey: selectVisualizationDisplayKey,
    sheetNeedsInit,
})

/** @type {(state: any, props: any) => any} */
export const selectCanRemoveRows = (state, props) =>
    props && props.canRemoveRows && props.dataType !== DjangIO.app.models.QuorumDataType.custom_data.value

/** @type {(state: any, props: any) => any} */
export const selectIsCustomDataSheet = createSelector(
    selectSheetDataType,
    (dataType) => dataType === DjangIO.app.models.QuorumDataType.custom_data.value,
)

/** @type {(state: any, props: any) => any} */
export const selectOverrideAllowDatumLink = (state, props) => props.overrideAllowDatumLink

/** @type {(state: any, props: any) => any} */
const selectAllowDatumLink = createSelector(
    selectSheetDataType,
    selectOverrideAllowDatumLink,
    (dataType, overrideAllowDatumLink) =>
        ![
            DjangIO.app.models.QuorumDataType.custom_data.value,
            DjangIO.app.models.QuorumDataType.state.value,
            DjangIO.app.models.QuorumDataType.district.value,
        ].includes(dataType) || overrideAllowDatumLink,
)

// This connection is used to provide props to Sheet.jsx
/** @type {(state: any, props: any) => any} */
export const sheetConnection = createStructuredSelector({
    allowDatumLink: selectAllowDatumLink,
    canRemoveRows: selectCanRemoveRows,
    columnsToRender: selectColumnsToRender,
    focusedColumnIndex: selectFocusedColumnIndex,
    isCustomDataSheet: selectIsCustomDataSheet,
    isDesignSystemResourceTable: selectIsResourceTable,
    isDevTeamMode: selectIsDevTeamMode,
    isReordable: selectIsReorderable,
    isResizable: selectCanRemoveColumns,
    rowHeight: selectRowHeight,
    searchingColumnIndexes: selectSearchingColumnIndexes,
    shouldDisplayMathBar: selectShouldDisplayMathBar,
    sortingColumnIndex: selectSortingColumnIndex,
    tableScrollThreshold: selectTableScrollThreshold,
    alignmentTypes: selectAlignmentTypes,
    shouldShowEmailAllButton: selectShouldShowEmailAllButton,
    emailAllParams: selectCreateEmailAllParams,
    stanceOptions: selectImmutableStancesWithCustomLabels,
})

// a split-off sheetConnection for the new sheet to incrementally
// minimize reliance on redux. Initially required because
// selectFilteredAndSortedRowData was giving the table
// insufficient rows in some cases due to inconsistencies in
// redux application state
/** @type {(state: any, props: any) => any} */
export const mantineSheetConnection = createStructuredSelector({
    allowDatumLink: selectAllowDatumLink,
    canRemoveRows: selectCanRemoveRows,
    columnsToRender: selectColumnsToRender,
    focusedColumnIndex: selectFocusedColumnIndex,
    isCustomDataSheet: selectIsCustomDataSheet,
    isReordable: selectIsReorderable,
    isResizable: selectCanRemoveColumns,
    rowHeight: selectRowHeight,
    searchingColumnIndexes: selectSearchingColumnIndexes,
    shouldDisplayMathBar: selectShouldDisplayMathBar,
    sortingColumnIndex: selectSortingColumnIndex,
    tableScrollThreshold: selectTableScrollThreshold,
    alignmentTypes: selectAlignmentTypes,
    shouldShowEmailAllButton: selectShouldShowEmailAllButton,
    emailAllParams: selectCreateEmailAllParams,
    stanceOptions: selectImmutableStancesWithCustomLabels,
    rowData: selectRowData,
})
